<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from '@/i18n'

import layoutShell from '@/components/layout/layoutShell.vue'
import LayoutFooter from '@/components/layout/layoutFooter.vue'

import compGroupDetailOverview    from '@/components/hackwatch/groups/comGroupDetailOverview.vue'
import compGroupDetailLocation    from '@/components/hackwatch/groups/compGroupDetailLocation.vue'
import compGroupDetailProfiles    from '@/components/hackwatch/groups/compGroupDetailProfiles.vue'
import compGroupSummaryKpis       from '@/components/hackwatch/groups/compGroupSummaryKpis.vue'
import compGroupDetailTTPsTable   from '@/components/hackwatch/groups/compGroupDetailTTPsTable.vue'
import compGroupDetailPostsTable  from '@/components/hackwatch/groups/compGroupDetailPostsTable.vue'
import compGroupDetailAliases     from '@/components/hackwatch/groups/compGroupDetailAliases.vue'
import compGroupTools             from '@/components/hackwatch/groups/compGroupTools.vue'
import compGroupWorkflowCoverage  from '@/components/hackwatch/groups/compGroupWorkflowCoverage.vue'
import compGroupDetailIOCs        from '@/components/hackwatch/iocs/compGroupDetailIOCs.vue'

const props = defineProps({
  groupId: { type: Number, default: null },
})

const route  = useRoute()
const router = useRouter()
const { t }  = useI18n()

const groupId = computed(() => props.groupId ?? Number(route.params.id))
</script>

<template>
  <layoutShell>
    <div class="layout-content-wrapper">
      <main class="content">

        <!-- Page header -->
        <div class="grid mb-3">
          <div class="col-12 flex align-items-center gap-3">
            <pButton
              icon="pi pi-arrow-left"
              severity="secondary"
              text
              size="small"
              v-tooltip.right="t('hackwatch.groups.title')"
              @click="router.back()"
            />
            <h1 class="m-0">{{ t('hackwatch.groupDetail.pageTitle') }}</h1>
          </div>
        </div>

        <!-- Row 1: Overview (links) + KPIs + Aliase (rechts) -->
        <div class="grid">
          <div class="col-12 md:col-6">
            <compGroupDetailOverview />
          </div>
          <div class="col-12 md:col-6">
            <div class="flex flex-column gap-3">
              <compGroupSummaryKpis :group-id="groupId" />
              <compGroupDetailAliases :group-id="groupId" />
            </div>
          </div>
        </div>

        <!-- Row 2: Profile + Locations -->
        <div class="grid mt-2">
          <div class="col-12 md:col-6">
            <compGroupDetailProfiles :group-id="groupId" />
          </div>
          <div class="col-12 md:col-6">
            <compGroupDetailLocation :group-id="groupId" />
          </div>
        </div>

        <!-- Row 3: Posts + TTPs -->
        <div class="grid mt-2">
          <div class="col-12 md:col-6">
            <compGroupDetailPostsTable :group-id="groupId" />
          </div>
          <div class="col-12 md:col-6">
            <compGroupDetailTTPsTable :group-id="groupId" />
          </div>
        </div>

        <!-- Row 4: Tools -->
        <div class="grid mt-2">
          <div class="col-12 md:col-6">
            <compGroupTools :groupId="groupId" />
          </div>

          <!-- Row 5: IOCs -->
          <div class="col-12 md:col-6">
            <compGroupDetailIOCs :group-id="groupId" />
          </div>
        </div>

        <!-- Row 6: SIEM Workflow Coverage -->
        <div class="grid mt-2">
          <div class="col-12">
            <compGroupWorkflowCoverage :group-id="groupId" />
          </div>
        </div>

      </main>
    </div>

    <div class="layout-footer">
      <LayoutFooter />
    </div>
  </layoutShell>
</template>

<style scoped>
h1 { font-weight: 700; letter-spacing: .01em; }
</style>
